import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PageShell from '../../components/PageShell';
import CardShell from '../../components/CardShell';
import SearchField from '../../components/SearchField';
import { Cell, Choix } from '../../components/InlineCells';
import { useP } from '../../i18n/useP';
import { formatMoney } from '../../utils/format';
import { usePersistentValue } from '../../hooks/usePersistentState';
import { useRegisterPageExports } from '../../exports/pageExports';
import { useViewExcelExport } from '../../components/ExcelExportButton';
import {
  getBudget, createBudgetLine, patchBudgetLine, deleteBudgetLine,
  loadBudgetAccounts, copyBudgetYear, clearBudgetYear,
} from '../../api/financials_budget';
import './BudgetChargesPage.css';

/* VENTES & OPS › OPÉRATIONS › CHARGES — le BUDGET DES CHARGES (décision
   Olivier, 2026-09-28). Une table unique, qu'on filtre et qu'on trie : le
   compte, le libellé, le responsable en texte libre, puis les valeurs. Le
   gabarit reprend celui des charges indirectes de la Simulation 12M (numéro,
   pastilles des mois avec TOUS, total marqué), jugé plus lisible.

   · La PÉRIODICITÉ bascule au clic, mensuel ou annuel, et l'annuel se teinte.
     La colonne « Par mois » est partie le 2026-09-29 (elle ne tenait pas sur
     une charge ponctuelle) : la MOYENNE MENSUELLE se lit sous le total annuel.
   · La charge tombe les mois cochés, sans lissage ; le paiement se décale de
     M−1, M ou M+1 (M par défaut).
   · Tout ce qui se calcule vient du SERVEUR (par mois, année, effet) : l'écran
     ne fait qu'afficher et envoyer ce qu'on tape. */

const LETTERS = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];
const MONTHS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
const ALL = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
/* L'utilisateur qui a saisi, en NOM COMPLET (demande d'Olivier, 2026-09-28) :
   les initiales du matin même disaient trop peu. */

export default function BudgetChargesPage({ activeTenant, year, addToast, canWrite = true }) {
  const p = useP();
  const tid = activeTenant?.id;
  /* L'unité est celle des Charges directes et variables (même mémoire par
     client) : les trois charges d'Opérations se lisent dans la même (2026-09-30). */
  const [unit, setUnit] = usePersistentValue(`fc_steps_unit_${tid}`, 'kchf');
  const pageUnit = useMemo(() => ({ value: unit, onChange: setUnit }), [unit, setUnit]);
  const k = unit === 'kchf';
  const fmt = (v) => (v == null ? '' : formatMoney(v, unit, 0));
  const unitLbl = k ? 'kCHF' : 'CHF';
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  const [sort, setSort] = useState(null);          // { k: 'acc'|'label'|'owner'|'year', d: 1|-1 }

  const load = useCallback(async () => {
    if (!tid || !year) return;
    const res = await getBudget(tid, year);
    if (res.error) { addToast && addToast(res.error, 'error'); return; }
    setData(res.data);
  }, [tid, year, addToast]);
  useEffect(() => { load(); }, [load]);

  const run = useCallback(async (promise) => {
    const res = await promise;
    if (res && res.error) addToast && addToast(res.error, 'error');
    await load();
  }, [load, addToast]);

  const exp = useViewExcelExport({ fileBase: p('Charges fixes'), tenantName: activeTenant?.name, year, addToast });
  const exportOptions = useMemo(() => [{ id: 'excel', label: 'Excel', run: exp.run }], [exp.run]);
  useRegisterPageExports(exportOptions);

  const accounts = useMemo(() => data?.accounts || [], [data]);
  const accLabel = useMemo(() => Object.fromEntries(accounts.map((a) => [a.id, a.label])), [accounts]);
  /* PREMIÈRE OUVERTURE (demande d'Olivier, 2026-09-28) : un exercice sans
     ligne montre d'office une ligne par compte de la structure, montant à
     saisir. Rien ne s'enregistre à la lecture (une version verrouillée se lit
     sans écrire) : la première saisie pose toutes les lignes, puis s'applique
     à la sienne. Une ligne sans montant ne prend pas la main sur le compte. */
  const lines = useMemo(() => {
    if (!data) return [];
    if ((data.lines || []).length) return data.lines;
    return (data.accounts || []).map((a) => ({
      id: `new-${a.id}`, virtual: true, reporting_line_id: a.id, label: '', entered_by: '',
      amount: null, periodicity: 'monthly', payment_offset: 0, vat: !a.no_vat, months: ALL,
      per_month: null, year_total: 0,
    }));
  }, [data]);
  const rows = useMemo(() => {
    const all = lines;
    const needle = q.trim().toLowerCase();
    let out = needle
      ? all.filter((l) => `${accLabel[l.reporting_line_id] || ''} ${l.label} ${l.entered_by}`.toLowerCase().includes(needle))
      : all;
    if (sort) {
      const key = { acc: (l) => accLabel[l.reporting_line_id] || '', label: (l) => l.label, by: (l) => l.entered_by || '', year: (l) => l.year_total }[sort.k];
      out = [...out].sort((a, b) => {
        const x = key(a); const y = key(b);
        return (x > y ? 1 : x < y ? -1 : 0) * sort.d;
      });
    }
    return out;
  }, [lines, q, sort, accLabel]);

  if (!tid) return null;
  const patch = async (l, body) => {
    if (!l.virtual) return run(patchBudgetLine(tid, l.id, body));
    const made = await loadBudgetAccounts(tid, year);
    if (made && made.error) { addToast && addToast(made.error, 'error'); return load(); }
    const res = await getBudget(tid, year);
    const real = (res.data?.lines || []).find((x) => x.reporting_line_id === l.reporting_line_id);
    return real ? run(patchBudgetLine(tid, real.id, body)) : load();
  };
  const toggleMonth = (l, m) => {
    const has = l.months.includes(m);
    const next = has ? l.months.filter((x) => x !== m) : [...l.months, m].sort((a, b) => a - b);
    patch(l, { months: next });
  };
  const sortBy = (k) => setSort((s) => (s && s.k === k ? (s.d > 0 ? { k, d: -1 } : null) : { k, d: 1 }));
  const arrow = (k) => (sort && sort.k === k ? (sort.d > 0 ? ' ▲' : ' ▼') : '');
  const total = rows.reduce((s, l) => s + (l.year_total || 0), 0);
  const eff = data?.effect || { charges_ht: Array(12).fill(0), cash_ttc: Array(12).fill(0) };
  const sum = (a) => a.reduce((s, v) => s + (v || 0), 0);

  const actions = canWrite ? (
    <span className="bud-acts">
      <button type="button" className="lk-btn lk-btn--quiet" onClick={() => run(loadBudgetAccounts(tid, year))}>
        {p('Une ligne par compte')}
      </button>
      <button type="button" className="lk-btn lk-btn--quiet"
              onClick={() => {
                if (window.confirm(`${p('Remplacer les lignes de')} ${year} ${p('par celles de')} ${year - 1} ?`)) run(copyBudgetYear(tid, year - 1, year));
              }}>
        {p('Copier')} {year - 1}
      </button>
      <button type="button" className="lk-btn lk-btn--quiet"
              onClick={() => {
                if (window.confirm(`${p('Supprimer toutes les lignes de')} ${year} ?`)) run(clearBudgetYear(tid, year));
              }}>
        {p("Vider l'année")}
      </button>
      <button type="button" className="lk-btn"
              disabled={!accounts.length}
              onClick={() => run(createBudgetLine(tid, { year, reporting_line_id: accounts[accounts.length - 1]?.id }))}>
        ＋ {p('Ligne')}
      </button>
    </span>
  ) : null;

  return (
    <PageShell kind="entry" title={p('Charges fixes')} unit={pageUnit} exportRef={exp.ref}>
      <CardShell title={p('Charges fixes')} subtitle={`${year} · ${unitLbl}`} primaryAction={actions}
                 search={<SearchField value={q} onChange={setQ} placeholder={p('Rechercher…')} />}
                 filterCount={q.trim() ? 1 : 0} collapsible storageKey="fin-budget"
                 flush>
        <div className="lk-hscroll">
          <table className="lk-entry-table bud-tbl">
            <colgroup>
              <col style={{ width: 34 }} /><col style={{ width: 24 }} /><col style={{ width: 190 }} />
              <col style={{ width: 200 }} /><col style={{ width: 140 }} /><col style={{ width: 96 }} />
              <col style={{ width: 88 }} /><col style={{ width: 70 }} /><col style={{ width: 44 }} />
              <col style={{ width: 318 }} /><col style={{ width: 92 }} />
            </colgroup>
            <thead>
              <tr>
                <th className="bud-c">#</th>
                <th />
                <th className="bud-s" onClick={() => sortBy('acc')}>{p('Compte')}{arrow('acc')}</th>
                <th className="bud-s" onClick={() => sortBy('label')}>{p('Libellé')}{arrow('label')}</th>
                <th className="bud-s" title={p('Saisi par')} onClick={() => sortBy('by')}>{p('Par')}{arrow('by')}</th>
                <th className="bud-r">{p('Montant HT')}</th>
                <th className="bud-c">{p('Périodicité')}</th>
                <th className="bud-c">{p('Paiement')}</th>
                <th className="bud-c">{p('TVA')}</th>
                <th>{p('Mois de la charge')}</th>
                <th className="bud-r bud-s" onClick={() => sortBy('year')}>{p('Année')}{arrow('year')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((l, i) => (
                <tr key={l.id}>
                  <td className="bud-c bud-n">{i + 1}</td>
                  {/* La croix DEVANT le compte (demande d'Olivier, 2026-09-28). */}
                  <td className="lk-actions">
                    {canWrite && !l.virtual && (
                      <button type="button" className="lk-x lk-del" aria-label={p('Supprimer')}
                              onClick={() => run(deleteBudgetLine(tid, l.id))}>✕</button>
                    )}
                  </td>
                  <td>
                    <Choix value={l.reporting_line_id} canWrite={canWrite}
                           options={accounts.map((a) => ({ value: a.id, label: a.label }))}
                           onSave={(v) => v && patch(l, { reporting_line_id: v })} />
                  </td>
                  <td><Cell value={l.label} canWrite={canWrite} placeholder="—" onSave={(v) => patch(l, { label: v })} /></td>
                  <td className="bud-by" title={l.entered_by || ''}>{l.entered_by || ''}</td>
                  {/* Montant attendu : « à saisir » sur fond ambre (standard `.lk-todo`),
                      porté par le texte lui-même, jamais par une enveloppe. */}
                  <td className={`bud-r${l.amount == null ? ' bud-todo' : ''}`}>
                    {l.amount == null && !canWrite ? <span className="lk-todo">{p('à saisir')}</span> : (
                      <Cell value={l.amount} type="number" format={fmt} canWrite={canWrite} placeholder={p('à saisir')}
                            toDraft={(v) => (v == null ? '' : String(k ? v / 1000 : v))}
                            onSave={(v) => patch(l, v == null ? { clear_amount: true } : { amount: k ? v * 1000 : v })} />
                    )}
                  </td>
                  <td className="bud-c">
                    <button type="button" disabled={!canWrite}
                            className={`bud-per${l.periodicity === 'annual' ? ' is-annual' : ''}`}
                            onClick={() => patch(l, { periodicity: l.periodicity === 'annual' ? 'monthly' : 'annual' })}>
                      {l.periodicity === 'annual' ? p('annuel') : p('mensuel')}
                    </button>
                  </td>
                  <td className="bud-c">
                    <Choix value={String(l.payment_offset)} canWrite={canWrite}
                           options={[{ value: '-1', label: 'M−1' }, { value: '0', label: 'M' }, { value: '1', label: 'M+1' }]}
                           onSave={(v) => patch(l, { payment_offset: Number(v || 0) })} />
                  </td>
                  <td className="bud-c">
                    <input type="checkbox" checked={l.vat} disabled={!canWrite}
                           aria-label={p('TVA récupérable')} onChange={(e) => patch(l, { vat: e.target.checked })} />
                  </td>
                  <td>
                    <span className="bud-chips">
                      <button type="button" disabled={!canWrite}
                              className={`bud-chip bud-chip--all${l.months.length === 12 ? ' is-on' : ''}`}
                              onClick={() => patch(l, { months: l.months.length === 12 ? [] : ALL })}>{p('TOUS')}</button>
                      {LETTERS.map((c, m) => (
                        <button key={m} type="button" disabled={!canWrite} title={MONTHS[m]}
                                className={`bud-chip${l.months.includes(m) ? ' is-on' : ''}`}
                                onClick={() => toggleMonth(l, m)}>{c}</button>
                      ))}
                    </span>
                  </td>
                  <td className="bud-r">{fmt(l.year_total)}</td>
                </tr>
              ))}
              <tr className="bud-total">
                <td /><td />
                <td colSpan={8}>{p('Total')}{q.trim() ? ` · ${p('filtré')}` : ''}</td>
                <td className="bud-r">{fmt(total)}</td>
              </tr>
              <tr className="bud-avg">
                <td /><td />
                <td colSpan={8}>{p('Moyenne mensuelle')}</td>
                <td className="bud-r">{fmt(total / 12)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </CardShell>

      <CardShell title={p("Effet sur l'exercice")} subtitle={`${year} · ${unitLbl}`} flush>
        <div className="lk-hscroll">
          <table className="lk-entry-table bud-eff">
            <colgroup>
              <col style={{ width: 150 }} />
              {MONTHS.map((m) => <col key={m} style={{ width: 78 }} />)}
              <col style={{ width: 96 }} />
            </colgroup>
            <thead>
              <tr><th />{MONTHS.map((m) => <th key={m} className="bud-r">{m}</th>)}<th className="bud-r">{p('Total')}</th></tr>
            </thead>
            <tbody>
              <tr>
                <td>{p('Charges, HT')}</td>
                {eff.charges_ht.map((v, i) => <td key={i} className="bud-r">{fmt(v)}</td>)}
                <td className="bud-r bud-b">{fmt(sum(eff.charges_ht))}</td>
              </tr>
              <tr>
                <td>{p('Décaissé, TTC')}</td>
                {eff.cash_ttc.map((v, i) => <td key={i} className="bud-r">{fmt(v)}</td>)}
                <td className="bud-r bud-b">{fmt(sum(eff.cash_ttc))}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </CardShell>
    </PageShell>
  );
}
